import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { CalendarPlus, Trash2, Clock, Users } from "lucide-react";

const PRESETS = [["16:00", "16:30"], ["16:30", "17:00"], ["19:30", "20:00"], ["20:00", "20:30"]];

const fmtMin = (m) => `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export default function MentorAvailability({ mentorId, slots, onChanged }) {
  const confirm = useConfirm();
  const [allowance, setAllowance] = useState(null);
  const [date, setDate] = useState("");
  const [start, setStart] = useState("16:00");
  const [end, setEnd] = useState("16:30");
  const [busy, setBusy] = useState(false);

  const loadAllowance = () => api.get("/mentor/allowance").then(({ data }) => setAllowance(data)).catch(() => {});
  useEffect(() => { loadAllowance(); }, [slots.length]);

  const upcoming = useMemo(() => slots
    .filter((s) => new Date(s.slot_end.endsWith("Z") || s.slot_end.includes("+") ? s.slot_end : s.slot_end + "Z") > new Date())
    .sort((a, b) => a.slot_start.localeCompare(b.slot_start)), [slots]);

  const add = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Pick a date");
    setBusy(true);
    try {
      await api.post("/mentor-slots", { mentor_id: mentorId, slot_start: `${date}T${start}:00Z`, slot_end: `${date}T${end}:00Z` });
      toast.success(`Slot added · ${fmtDate(date)} ${start}–${end}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add slot");
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!(await confirm({
      title: `Remove ${fmtDate(s.day_date)} ${s.slot_start.slice(11, 16)}–${s.slot_end.slice(11, 16)}?`,
      description: s.booked_by_team_id ? `${s.team_name || "A team"} has booked this — they'll be told to re-book.` : "This slot is free; nobody will be affected.",
      destructive: true, confirmText: "Remove slot",
    }))) return;
    try {
      await api.delete(`/mentor-slots/${s.id}`);
      toast.success("Slot removed");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <section className="border-2 border-[#0A0A0A] bg-white p-6 space-y-5" data-testid="mentor-availability-panel">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><CalendarPlus size={12} /> My availability · online support</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Offer times that suit you and your team</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
          Add the times you're free between <strong>5 Nov and 8 Dec</strong>; your team books from these. Sessions on 29 Oct and 4 Nov are in person and don't count.
          You have <strong>8 hours</strong> of online support in total{allowance?.teams_count > 1 ? `, split ${fmtMin(allowance.per_team_min)} per team` : ""}. Going beyond that is entirely your call.
        </p>
      </div>

      {allowance && allowance.teams.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="mentor-allowance-meters">
          {allowance.teams.map((t) => {
            const pct = Math.min(100, Math.round((t.used_min / t.allowance_min) * 100));
            const over = t.used_min > t.allowance_min;
            return (
              <div key={t.id} className="border border-[#E5E7EB] p-3" data-testid={`mentor-allowance-team-${t.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-[#0F1F2E] inline-flex items-center gap-1.5"><Users size={11} /> {t.name}</span>
                  <span className={over ? "text-[#A23232] font-semibold" : "text-[#525252]"}>{fmtMin(t.used_min)} of {fmtMin(t.allowance_min)} booked</span>
                </div>
                <div className="h-1.5 bg-[#F3F4F6] mt-2"><div className={`h-full ${over ? "bg-[#e93e36]" : "bg-[#25AAAA]"}`} style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={add} className="border border-[#E5E7EB] bg-[#FAFAFA] p-4 grid gap-3 sm:grid-cols-[1fr_auto_auto_auto] items-end" data-testid="mentor-add-slot-form">
        <div>
          <label className="label-eyebrow block mb-1">Date</label>
          <Input type="date" value={date} min="2026-11-05" max="2026-12-08" onChange={(e) => setDate(e.target.value)} className="rounded-none h-10" data-testid="mentor-add-slot-date" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Start</label>
          <Input type="time" step="900" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-none h-10 w-28" data-testid="mentor-add-slot-start" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">End</label>
          <Input type="time" step="900" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-none h-10 w-28" data-testid="mentor-add-slot-end" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-none h-10 bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="mentor-add-slot-btn">
          {busy ? "Adding…" : "Add slot"}
        </Button>
        <div className="sm:col-span-4 flex flex-wrap gap-1.5 items-center text-[10px] text-[#525252]">
          Quick pick (UK time):
          {PRESETS.map(([a, b]) => (
            <button type="button" key={a} onClick={() => { setStart(a); setEnd(b); }}
              className={`px-2 py-1 border ${start === a && end === b ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
              data-testid={`mentor-slot-preset-${a.replace(":", "")}`}>{a}–{b}</button>
          ))}
        </div>
      </form>

      {upcoming.length === 0 ? (
        <div className="text-sm text-[#525252] italic" data-testid="mentor-availability-empty">No upcoming slots yet — add your first above.</div>
      ) : (
        <ul className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]" data-testid="mentor-availability-list">
          {upcoming.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`mentor-availability-slot-${s.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Clock size={13} className="text-[#525252] shrink-0" />
                <span className="font-medium text-[#0F1F2E]">{fmtDate(s.day_date)}</span>
                <span className="text-[#525252]">{s.slot_start.slice(11, 16)}–{s.slot_end.slice(11, 16)}</span>
                {s.booked_by_team_id
                  ? <span className="text-[10px] uppercase tracking-wider bg-[#0F1F2E] text-white px-1.5 py-0.5 font-bold truncate">{s.team_name || "Booked"}</span>
                  : <span className="text-[10px] uppercase tracking-wider text-[#25AAAA] font-bold">Free</span>}
              </div>
              <button onClick={() => remove(s)} className="text-[#A23232] hover:text-[#7A1F1F]" title="Remove slot" data-testid={`mentor-availability-remove-${s.id}`}>
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
